import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Package, 
  FileText, 
  Users, 
  Mail, 
  TrendingUp, 
  Eye,
  ArrowUpRight,
  AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-slate-200 rounded-sm" />
          ))}
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Engines',
      value: stats?.total_engines || 0,
      subValue: `${stats?.active_engines || 0} active`,
      icon: Package,
      color: 'bg-blue-500',
    },
    {
      title: 'Quote Requests',
      value: stats?.total_quotes || 0,
      subValue: `${stats?.pending_quotes || 0} pending`,
      icon: FileText,
      color: 'bg-orange-500',
      alert: stats?.pending_quotes > 0,
    },
    {
      title: 'Newsletter Subscribers',
      value: stats?.total_subscribers || 0,
      icon: Mail,
      color: 'bg-emerald-500',
    },
    {
      title: 'Contact Messages',
      value: stats?.total_contacts || 0,
      subValue: `${stats?.unread_contacts || 0} unread`,
      icon: Users,
      color: 'bg-purple-500',
      alert: stats?.unread_contacts > 0,
    },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
        <p className="text-slate-600">Overview of your marine engine catalog</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, idx) => {
          const Icon = stat.icon;
          return (
            <Card key={idx} className="relative overflow-hidden">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">{stat.title}</p>
                    <p className="text-3xl font-bold text-slate-900">{stat.value.toLocaleString()}</p>
                    {stat.subValue && (
                      <p className="text-sm text-slate-500 mt-1">{stat.subValue}</p>
                    )}
                  </div>
                  <div className={`w-12 h-12 ${stat.color} rounded-sm flex items-center justify-center`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                </div>
                {stat.alert && (
                  <div className="absolute top-2 right-2">
                    <span className="flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Viewed Engines */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-orange-500" />
              Most Viewed Engines
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.top_engines?.length > 0 ? (
              <div className="space-y-4">
                {stats.top_engines.map((engine, idx) => (
                  <div key={engine.id} className="flex items-center gap-4">
                    <span className="w-8 h-8 bg-slate-100 rounded-sm flex items-center justify-center text-sm font-semibold text-slate-600">
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">{engine.title}</p>
                    </div>
                    <Badge variant="secondary" className="font-mono">
                      {engine.views?.toLocaleString()} views
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">No data available</p>
            )}
          </CardContent>
        </Card>

        {/* Top Brands */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-orange-500" />
              Engines by Brand
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.top_brands?.length > 0 ? (
              <div className="space-y-4">
                {stats.top_brands.map((brand, idx) => (
                  <div key={brand.brand} className="flex items-center gap-4">
                    <span className="w-8 h-8 bg-slate-100 rounded-sm flex items-center justify-center text-sm font-semibold text-slate-600">
                      {idx + 1}
                    </span>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{brand.brand}</p>
                    </div>
                    <Badge variant="secondary" className="font-mono">
                      {brand.count} engines
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">No data available</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
